/**
 * Command Palette
 *
 * Spotlight/Notion-style global search powered by cmdk + Chakra Modal.
 * Three usage modes:
 *   - "navigate" (default): Global search — results navigate to entities
 *   - "select": Entity picker — results returned to caller via onSelectEntity
 *   - Parent selection: Destination picker for move/copy via onSelectParent
 *
 * Features:
 *   - Server-side ranked search with TanStack Query caching (keepPreviousData).
 *     Keyword rows paint first; meaning matches arrive separately into Related
 *     (`paletteSections.ts`), so the embedding call never delays an exact match.
 *   - "Jump back in" (what you created or edited) when the query is empty
 *   - Entity type filter chips (Collections, Notes, Attachments)
 *   - Full keyboard navigation (arrow keys, Enter, Escape)
 *   - Accessible (ARIA combobox via cmdk + dialog via Chakra Modal)
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, IconButton, Kbd, Modal, ModalOverlay, ModalContent, HStack } from '@chakra-ui/react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router';
import { gradientVariantForGlyph } from '../../../theme/iconGradient';
import { BowerIcon } from '../../ui/BowerIcon';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { SearchScopeFilter, scopeShowsContent, type SearchScope } from './SearchScopeFilter';
import { SearchResultsList, searchFailureMessage, type ResultSection } from './SearchResultsList';
import { buildPaletteSections, WORKSPACE_PREVIEW, type PaletteSections } from './paletteSections';
import { ExpandedResultGroups } from './ExpandedResultGroups';
import { AskBirdFooter } from './AskBirdFooter';
import { usePaletteSelection } from './usePaletteSelection';
import { SearchLiveRegion } from './SearchFooter';
import { ParentModeList } from './ParentModeList';
import { useInfiniteSearchQuery, useSearchQuery } from '../../../hooks/data/useSearchQuery';
import { useStalled } from '../../../hooks/ui/useStalled';
import { useRecents } from '../../../hooks/data/useRecents';
import { useParentOptions } from '../../../hooks/data/useParentOptions';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import type { EntityType, SearchResult } from '../../../services/search.service';
import type { AnchorHint } from './ExpandedResultGroups';
import type { ParentEntityType, ParentOption } from './parentOptions.types';
import { isAttachmentLike, getArtifactRoute } from '../../../utils/artifactCapabilities';
import { safeAnalytics } from '../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { openBirdChat } from '../../../services/events/birdChatEvents';
import { ROUTES } from '../../../constants/routes';
import { openLinkArtifact } from '../../../hooks/entity/openLinkArtifact';
import { searchGroupRoute } from '../../../utils/searchResultGroup';
import {
  useSetShowCommandPaletteFilters,
  useShowCommandPaletteFilters,
} from '../../../stores/uiStore';
import './commandPalette.css';
import { BowerSpinner } from '../../ui/BowerSpinner';

export type { ParentEntityType, ParentOption };

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  /** Land on this scope when opened from a surface that knows the intent. */
  initialScope?: SearchScope | null;
  mode?: 'navigate' | 'select';
  onSelectEntity?: (result: SearchResult) => void;
  onSelectParent?: (parentType: ParentEntityType, parentId: string) => void | Promise<void>;
  /**
   * Parent mode with the whole chosen option (its name too, for a toast),
   * in place of `onSelectParent`. Separate on purpose: several callers pass
   * a handler whose optional THIRD parameter means something else.
   */
  onSelectParentOption?: (option: ParentOption) => void | Promise<void>;
  onOpenAttachment?: (artifactId: string) => void;
  entityTypes?: EntityType[];
  currentParentId?: string;
  /**
   * Parent mode: destinations never to offer (the workspace id hides the
   * root). The workspace canvas passes the folders being moved and their
   * shared current parent; every other caller omits it.
   */
  excludeParentIds?: ReadonlyArray<string>;
  placeholder?: string;
}

/**
 * Fire SEARCH_PERFORMED once per debounced query, when its answer has SETTLED,
 * with the measures search quality is judged on: how many results (0 is the
 * zero-result rate), whether the top row was a name match, and how long the
 * answer took from the debounced query. IDs, counts and enums only, never the
 * query text (`rules/analytics.md`).
 */
function useTrackSearchSettled(args: {
  debouncedQuery: string;
  settled: boolean;
  results: readonly SearchResult[];
  mode: 'navigate' | 'select';
  isParentMode: boolean;
}): void {
  const { debouncedQuery, settled, results, mode, isParentMode } = args;
  const askedAtRef = useRef<{ query: string; at: number }>({ query: '', at: 0 });
  const reportedRef = useRef('');
  if (debouncedQuery !== askedAtRef.current.query) {
    askedAtRef.current = { query: debouncedQuery, at: performance.now() };
    // A new ask: the same text asked again later is a new search to report.
    reportedRef.current = '';
  }
  useEffect(() => {
    if (!settled || !debouncedQuery || reportedRef.current === debouncedQuery) return;
    reportedRef.current = debouncedQuery;
    safeAnalytics.track(ANALYTICS_EVENTS.SEARCH_PERFORMED, {
      query_length: debouncedQuery.length,
      query_words: debouncedQuery.trim().split(/\s+/).length,
      search_type: 'command_palette',
      mode: isParentMode ? 'parent' : mode,
      result_count: results.length,
      top_match_tier: results[0]?.match?.tier,
      latency_ms: Math.round(performance.now() - askedAtRef.current.at),
    } as Record<string, unknown>);
  }, [settled, debouncedQuery, results, mode, isParentMode]);
}

/**
 * Rows actually on screen: the preview shows its cap, not every row the page
 * held, and Related counts only where it renders (the same rule as the list:
 * in the full list, only once it has stopped growing).
 */
function shownCount(
  sections: PaletteSections | null,
  results: readonly SearchResult[],
  relatedOnScreen: boolean,
): number {
  if (!sections) return results.length;
  return sections.rows.length + (relatedOnScreen ? sections.related.length : 0);
}

/** The row the highlight should rest on first: the top of whatever is shown. */
function firstShownId(sections: PaletteSections | null, results: readonly SearchResult[]) {
  return sections ? sections.rows[0]?.id : results[0]?.id;
}

const EMPTY_RESULTS: SearchResult[] = [];

/** Meaning matches start at three characters; shorter says too little to embed. */
const MEANING_MIN_CHARS = 3;
/**
 * The meaning request waits for a real pause, not the keyword 200 ms: each one
 * is an embedding call (up to 2.5 s server-side) that a client abort does not
 * stop, so firing it inside a word pays for answers nobody sees.
 */
const MEANING_DEBOUNCE_MS = 450;

/** Entity-type filter for the search query: parent mode forces projects only. */
function resolveEntityTypes(
  isParentMode: boolean,
  fixedEntityTypes: EntityType[] | undefined,
  activeFilters: EntityType[],
): EntityType[] | undefined {
  if (isParentMode) return ['project'];
  if (fixedEntityTypes && fixedEntityTypes.length > 0) return fixedEntityTypes;
  return activeFilters.length > 0 ? activeFilters : undefined;
}

/**
 * How many results to announce, or null for "don't announce a count".
 *
 * The count describes CONTENT results, so it is meaningless where content
 * isn't rendered — under Navigation / Settings / Help the list holds command
 * rows and this number described something off screen ("18 results" above two).
 *
 * And none for a failed request, in either mode: it counted nothing, so a
 * number would only restate the failure as "0 results".
 */
function countAnnounceable(
  isParentMode: boolean,
  parentCount: number,
  showsContent: boolean,
  contentCount: number,
  failed: boolean,
): number | null {
  if (failed) return null;
  if (isParentMode) return parentCount;
  return showsContent ? contentCount : null;
}

/**
 * The content row the highlight may anchor to, or undefined when the content
 * list isn't on screen.
 *
 * Scope-gated for the same reason the child gates topId/firstStaticId: under
 * Navigation / Settings / Help the content list isn't rendered at all, so
 * anchoring to a content row leaves the highlight on nothing and Enter dead.
 * `countAnnounceable` above already got this right; the anchor didn't.
 */
function anchorableContentId(
  showsContent: boolean,
  firstId: string | undefined,
): string | undefined {
  return showsContent ? firstId : undefined;
}

/** The state of one content request, for {@link resolveContentFailure}. */
interface ContentRequest {
  readonly error: Error | null;
  readonly isFetching: boolean;
}

/**
 * The content request's error when it needs saying, else null.
 *
 * A request that failed is not an answer. Without this the content zone
 * collapsed and the footer said "0 results" for a 429 (staging, 2026-10-05).
 * With results on screen, only a failed LATER page counts: page 1 still
 * stands, and the page that did not arrive must not pass for the end of the
 * list. Null while a retry is in flight, so the loading state shows instead.
 */
function resolveContentFailure(
  isShowingRecents: boolean,
  hasResults: boolean,
  recents: ContentRequest,
  search: ContentRequest & { readonly nextPageFailed: boolean },
): Error | null {
  const active = isShowingRecents ? recents : search;
  if (active.isFetching) return null;
  if (hasResults) return !isShowingRecents && search.nextPageFailed ? search.error : null;
  return active.error;
}

/** Input placeholder for the palette's three modes (kept flat — no nested ternary). */
function resolvePlaceholder(
  custom: string | undefined,
  isParentMode: boolean,
  mode: 'navigate' | 'select',
): string {
  if (custom) return custom;
  if (isParentMode) return 'Move to...';
  return mode === 'select' ? 'Search to select...' : 'Search...';
}

export function CommandPalette({
  isOpen,
  onClose,
  initialScope,
  mode = 'navigate',
  onSelectEntity,
  onSelectParent,
  onSelectParentOption,
  onOpenAttachment,
  entityTypes: fixedEntityTypes,
  currentParentId,
  excludeParentIds,
  placeholder: customPlaceholder,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<EntityType[]>([]);
  const [scope, setScope] = useState<SearchScope>(initialScope ?? 'all');

  // Re-apply when the palette is reopened from a scope-specific entry point.
  // Keyed on isOpen so reopening from the chat panel lands on Chats again even
  // if the user had narrowed elsewhere during the previous session.
  const lastAppliedScopeRef = useRef<SearchScope | null>(null);
  useEffect(() => {
    if (!isOpen) {
      lastAppliedScopeRef.current = null;
      return;
    }
    if (initialScope && lastAppliedScopeRef.current !== initialScope) {
      lastAppliedScopeRef.current = initialScope;
      setScope(initialScope);
    }
  }, [isOpen, initialScope]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const showCommandPaletteFilters = useShowCommandPaletteFilters();
  const setShowCommandPaletteFilters = useSetShowCommandPaletteFilters();
  const navigate = useNavigate();
  const { workspace } = useWorkspace();

  const isParentMode = !!onSelectParent || !!onSelectParentOption;

  // Expanded Cmd-K palette (Top result / Go to / Settings / Ask Bower) renders
  // ONLY in navigate mode — never in the select or parent entity pickers.
  const expanded = !isParentMode && mode === 'navigate';

  // Anchor hint reported up from ExpandedResultGroups (which owns the command
  // registry). Kept here — not the registry hook itself — so the auth/query-
  // dependent registry only runs when the expanded palette is actually mounted;
  // select and parent-picker palettes keep provider-light dependencies.
  const [anchorHint, setAnchorHint] = useState<AnchorHint>({
    topId: null,
    firstStaticId: null,
    staticRows: [],
  });

  /**
   * Idempotent: a new object with the SAME ids must not re-render.
   *
   * The child reports anchors from an effect that rebuilds the object each
   * run, and its inputs come from a memo keyed on a context value. If anything
   * upstream churns identity per render — which a context provider returning a
   * fresh value does by default — passing `setAnchorHint` straight in is an
   * infinite loop: effect → setState → render → effect. Comparing before
   * setting makes the palette immune to that regardless of what upstream does.
   */
  const handleAnchorChange = useCallback((hint: AnchorHint) => {
    setAnchorHint((prev) =>
      prev.topId === hint.topId &&
      prev.firstStaticId === hint.firstStaticId &&
      prev.staticRows.length === hint.staticRows.length &&
      prev.staticRows.every((row, i) => row.id === hint.staticRows[i]?.id)
        ? prev
        : hint,
    );
  }, []);

  // Determine entity type filters: parent mode forces projects only
  const effectiveEntityTypes = useMemo(
    () => resolveEntityTypes(isParentMode, fixedEntityTypes, activeFilters),
    [isParentMode, fixedEntityTypes, activeFilters],
  );
  // Anything that replaces the results list (scope, Types, query) starts it at
  // the top again; a page loading for the same key keeps the reader's place.
  const listResetKey = `${scope}\u0000${effectiveEntityTypes?.join(',') ?? ''}\u0000${query}`;

  // Filter-row visibility, needed here because it decides the layout: the All
  // tab previews the top Workspace rows, the Workspace tab (only reachable
  // with the row on screen) shows the whole list. Entity pickers always show
  // the whole list; they have no "Show all" to reach anything past a cap.
  const supportsFilters = !isParentMode && (!fixedEntityTypes || fixedEntityTypes.length === 0);
  const showFilterRow = supportsFilters && (!expanded || showCommandPaletteFilters);
  const showAllResultZones = expanded && !showFilterRow;
  // A palette without the filter row (fixed entity types) has no Workspace tab
  // for "Show all" to open, so it shows the full list from the start.
  const layout: 'preview' | 'full' =
    expanded && supportsFilters && !(scope === 'workspace' && showFilterRow) ? 'preview' : 'full';

  // One ranked list, every type mixed, from one paged keyword query: the All
  // preview and the Workspace tab read the same pages, so switching between
  // them never refetches. Meaning matches are their own request, so the
  // embedding call never holds the ranked rows.
  const paged = useInfiniteSearchQuery(query, {
    entityTypes: effectiveEntityTypes,
    pageSize: 20,
    enabled: isOpen,
    // Folded runs only in the global search; a picker must offer each capture.
    groupGenerated: expanded,
  });
  const meaning = useSearchQuery(query, {
    entityTypes: effectiveEntityTypes,
    limit: 10,
    mode: 'semantic',
    debounceMs: MEANING_DEBOUNCE_MS,
    // Only where Workspace content is on screen: each ask is an embedding call.
    enabled: isOpen && expanded && scopeShowsContent(scope, showAllResultZones),
    minLength: MEANING_MIN_CHARS,
    groupGenerated: expanded,
  });
  const searchResults = paged.results;
  const searchTotal = paged.total;
  const isFetching = paged.isFetching;
  const searchError = paged.error;
  const refetchSearch = paged.refetch;
  const debouncedQuery = paged.debouncedQuery;
  const { isFetchingNextPage, hasNextPage, fetchNextPage, isFetchNextPageError } = paged;

  // Empty state. Gated on isOpen — CommandPalette is mounted (closed) by the
  // shell plus most list/detail pages, so an ungated recents query here fired
  // several duplicate /api/search calls on every navigation for a dialog
  // nobody had opened. The global palette leads with "Jump back in" (what
  // this user created or edited); a user with no history yet, and the
  // pickers, get the workspace's recent items.
  const mine = useRecents(8, effectiveEntityTypes, undefined, {
    enabled: isOpen && expanded,
    scope: 'mine',
    groupGenerated: expanded,
  });
  const usesWorkspaceRecents = !expanded || (mine.isSuccess && mine.data.length === 0);
  const workspaceRecents = useRecents(8, effectiveEntityTypes, undefined, {
    enabled: isOpen && usesWorkspaceRecents,
    groupGenerated: expanded,
  });
  const recents = usesWorkspaceRecents ? workspaceRecents : mine;
  const recentItems = useMemo(() => recents.data ?? [], [recents.data]);
  const recentsError = recents.error;
  const isFetchingRecents = recents.isFetching;
  const refetchRecents = recents.refetch;
  const recentsHeading = usesWorkspaceRecents ? 'Recent' : 'Jump back in';

  // Parent options: workspace + projects (only meaningful in parent mode)
  const parentSourceResults = query.trim().length === 0 ? recentItems : searchResults;
  const { filteredOptions: allParentOptions } = useParentOptions(
    isParentMode ? workspace : null,
    isParentMode ? parentSourceResults : [],
    currentParentId,
    query,
  );
  const parentOptions = useMemo(
    () =>
      excludeParentIds?.length
        ? allParentOptions.filter((option) => !excludeParentIds.includes(option.id))
        : allParentOptions,
    [allParentOptions, excludeParentIds],
  );

  // Choose which results to display, filtering out currentParentId
  const filteredResults = useMemo(() => {
    const display = query.trim().length === 0 ? recentItems : searchResults;
    if (!currentParentId) return display;
    return display.filter((r) => r.id !== currentParentId);
  }, [query, searchResults, recentItems, currentParentId]);

  // Meaning rows drop the current parent like the rest, and only the answer
  // for the query on screen shows: an earlier query's meaning rows (kept as
  // placeholder data while the next answer loads, or for a longer query after
  // backspacing below three characters) must not show as Related.
  const meaningResults =
    expanded &&
    query.trim().length >= MEANING_MIN_CHARS &&
    !meaning.isPlaceholderData &&
    // The meaning request waits out a longer debounce than keyword search,
    // so until it has asked for THIS query its answer is the previous one's.
    meaning.debouncedQuery === query
      ? meaning.results
      : EMPTY_RESULTS;
  const sections = useMemo(
    () =>
      !expanded || query.trim().length === 0
        ? null
        : buildPaletteSections(
            filteredResults,
            currentParentId
              ? meaningResults.filter((r) => r.id !== currentParentId)
              : meaningResults,
            searchTotal,
            layout === 'preview' ? WORKSPACE_PREVIEW : undefined,
          ),
    [expanded, query, filteredResults, meaningResults, currentParentId, searchTotal, layout],
  );

  // Handle navigating to an entity (palette is already closed when this runs)
  const handleNavigate = useCallback(
    (result: SearchResult) => {
      // A row standing for a Live run opens the run, which lists every capture.
      const runRoute = searchGroupRoute(result);
      if (runRoute) {
        navigate(runRoute);
      } else if (result.type === 'project') {
        navigate(ROUTES.collection(result.id));
      } else if ((result.type as string) === 'link') {
        // Search returns a link artifact as type 'link', which this union
        // predates. It opens its page in a new tab, never this one (link-artifacts D6).
        openLinkArtifact(result.id, { surface: 'command_palette' });
      } else if (isAttachmentLike(result.type) && onOpenAttachment) {
        onOpenAttachment(result.id);
      } else {
        const route = getArtifactRoute(result.type, result.id);
        if (route) navigate(route);
      }
    },
    [navigate, onOpenAttachment],
  );

  // Handle item selection (analytics + close palette + navigate or select)
  const handleSelect = useCallback(
    (result: SearchResult, section: ResultSection = 'list') => {
      const pool = section === 'related' ? (sections?.related ?? []) : filteredResults;
      const position = pool.findIndex((r) => r.id === result.id);
      safeAnalytics.track(ANALYTICS_EVENTS.SEARCH_RESULT_CLICKED, {
        result_type: result.type,
        result_category: 'content',
        result_section: section,
        result_position: position >= 0 ? position : undefined,
        match_tier: result.match?.tier,
        match_field: result.match?.field,
        query_length: query.length,
        mode,
      } as Record<string, unknown>);

      // Close palette first so navigation/side-panel opens cleanly
      onClose();

      if (mode === 'select' && onSelectEntity) {
        onSelectEntity(result);
      } else {
        handleNavigate(result);
      }
    },
    [sections, filteredResults, query, mode, onSelectEntity, handleNavigate, onClose],
  );

  // "Show all results in Workspace": the Workspace tab, the same list in
  // full. The tab lives on the filter row, so it is brought on screen too:
  // without it the person would have no visible way back to All.
  const handleShowAll = useCallback(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.SEARCH_SHOW_ALL_CLICKED, {
      query_length: query.length,
    });
    if (!showCommandPaletteFilters) setShowCommandPaletteFilters(true);
    setScope('workspace');
  }, [query, showCommandPaletteFilters, setShowCommandPaletteFilters]);

  // Handle parent selection (move/copy mode)
  const handleParentSelect = useCallback(
    async (option: ParentOption) => {
      onClose();
      if (onSelectParentOption) {
        await onSelectParentOption(option);
      } else if (onSelectParent) {
        await onSelectParent(option.type, option.id);
      }
    },
    [onSelectParent, onSelectParentOption, onClose],
  );

  useTrackSearchSettled({
    debouncedQuery,
    settled: !isFetching && !searchError && debouncedQuery.trim().length > 0,
    // The response for the debounced query, not what is on screen: the input
    // can be cleared inside the debounce, and then the screen shows recents.
    results: searchResults,
    mode,
    isParentMode,
  });

  // Reset state when closing
  const handleClose = useCallback(() => {
    setQuery('');
    setActiveFilters([]);
    onClose();
  }, [onClose, setActiveFilters]);

  // Hand the query off to Bower — mirrors AskBirdRow's self-contained dispatch so
  // the Tab shortcut and the row's mouse/Enter select produce the same event.
  const handleAskBird = useCallback(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.COMMAND_PALETTE_ASK_BIRD, {
      query_length: query.length,
    } as Record<string, unknown>);
    handleClose();
    openBirdChat({ source: 'command_palette', initialMessage: query });
  }, [query, handleClose]);

  const handleFilterVisibilityToggle = useCallback(() => {
    const visible = !showCommandPaletteFilters;
    setShowCommandPaletteFilters(visible);
    safeAnalytics.track(ANALYTICS_EVENTS.COMMAND_PALETTE_FILTER_VISIBILITY_TOGGLED, {
      visible,
      surface: 'global_search',
    });
    searchInputRef.current?.focus();
  }, [setShowCommandPaletteFilters, showCommandPaletteFilters]);

  // The persistent preference belongs to Cmd-K only. Entity pickers keep their
  // existing type control, avoiding a cross-surface preference that can hide a
  // picker capability the user did not change here.
  const canToggleFilterRow = expanded && supportsFilters;
  // With no visible scope controls the complete palette shows (`showAllResultZones`,
  // above) rather than leaving command groups behind a selector nobody can reach.
  const showsContent = scopeShowsContent(scope, showAllResultZones);

  // The highlight contract (controlled cmdk value + arrow-freeze + Tab hand-off)
  // lives in usePaletteSelection; the child reports Top/first-static ids up via
  // `anchorHint`, and content (priority 2) comes from `filteredResults`.
  const { selectedValue, setSelectedValue, handleCommandKeyDown, handlePointerMove } =
    usePaletteSelection({
      expanded,
      // A scope change is fresh intent too: the rows the highlight sat on changed.
      query: `${scope}\u0000${query}`,
      anchorHint,
      firstContentId: anchorableContentId(showsContent, firstShownId(sections, filteredResults)),
      onAskBird: handleAskBird,
    });
  // A spinner only once a search has been out longer than a fast answer takes.
  const showSpinner = useStalled(isFetching || meaning.isFetching);

  const isShowingRecents = query.trim().length === 0;
  const hasResults = filteredResults.length > 0;
  const contentFailure = resolveContentFailure(
    isShowingRecents,
    hasResults,
    { error: recentsError, isFetching: isFetchingRecents },
    { error: searchError, isFetching, nextPageFailed: isFetchNextPageError },
  );
  // Announced only where the failure row is on screen. Under Navigation /
  // Settings / Help the content zone is not rendered, so a spoken failure
  // there would have no visible row and no Retry (the countAnnounceable rule).
  const announcedFailure = isParentMode || showsContent ? contentFailure : null;
  // A failed page stops the sentinel. Left armed, the observer re-fires on
  // every render and re-asks with no bound, a 429 included.
  const canLoadMore = hasNextPage && !isFetchNextPageError;
  const showEmpty =
    !isShowingRecents && !hasResults && !isFetching && !contentFailure && debouncedQuery.length > 0;

  // Result count for footer: parent mode uses parentOptions count
  const resultCount = countAnnounceable(
    isParentMode,
    parentOptions.length,
    showsContent,
    shownCount(sections, filteredResults, layout === 'preview' || !canLoadMore),
    contentFailure !== null,
  );

  const handleRetryContent = useCallback(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.SEARCH_RETRIED, {
      surface: 'command_palette',
      mode: isParentMode ? 'parent' : mode,
    });
    if (isShowingRecents) void refetchRecents();
    else if (isFetchNextPageError) void fetchNextPage();
    else void refetchSearch();
  }, [
    isParentMode,
    isShowingRecents,
    isFetchNextPageError,
    mode,
    refetchRecents,
    fetchNextPage,
    refetchSearch,
  ]);

  // Placeholder text
  const placeholder = resolvePlaceholder(customPlaceholder, isParentMode, mode);

  // Non-parent content: global navigate palette versus the standard entity
  // picker list. Kept as a single ternary each to avoid nesting in the JSX.
  const contentSection = expanded ? (
    <ExpandedResultGroups
      query={query}
      scrollResetKey={listResetKey}
      mode={mode}
      onClose={handleClose}
      onAnchorChange={handleAnchorChange}
      scope={scope}
      showAllResultZones={showAllResultZones}
      isShowingRecents={isShowingRecents}
      hasResults={hasResults}
      isFetching={isFetching}
      filteredResults={filteredResults}
      sections={sections}
      onSelectContent={handleSelect}
      onShowAll={handleShowAll}
      layout={layout}
      recentsHeading={recentsHeading}
      hasNextPage={canLoadMore}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={() => fetchNextPage()}
      contentFailure={contentFailure}
      onRetryContent={handleRetryContent}
    />
  ) : (
    <SearchResultsList
      isShowingRecents={isShowingRecents}
      hasResults={hasResults}
      isFetching={isFetching}
      showEmpty={showEmpty}
      filteredResults={filteredResults}
      sections={sections}
      onSelect={handleSelect}
      query={query}
      recentsHeading={recentsHeading}
      hasNextPage={canLoadMore}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={() => fetchNextPage()}
      failure={contentFailure}
      onRetry={handleRetryContent}
      scrollResetKey={listResetKey}
    />
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} motionPreset="none" returnFocusOnClose size="xl">
      <ModalOverlay bg="bg.overlay" />
      <ModalContent
        // A white SHEET on the scrim, not a parchment one. Every other content
        // box went parchment in the frame flip; this is the exception the
        // `bg.surface` comment in semanticTokens.ts calls out — a surface
        // floating on its own elevation stays white, or a parchment menu over
        // a parchment sheet has nothing to separate it.
        bg="bg.elevated"
        // No border. At 24px radius with a real shadow the hairline only
        // muddies the corner.
        boxShadow="lg"
        borderRadius={{ base: 'xl', md: '3xl' }}
        w={{ base: '100%', md: '680px' }}
        maxW={{ base: '100%', md: '680px' }}
        // 96px from the top, as drawn — a fixed offset rather than a
        // percentage, so the sheet sits in the same place on a laptop and on a
        // 27" display instead of drifting down the taller one.
        mt={{ base: '8vh', md: '96px' }}
        mx={{ base: 2, md: 4 }}
        // `visible`, not `hidden`. The Types filter menu renders INSIDE this
        // box (portalling it out puts it beyond the modal's focus lock, which
        // then closes it on the click that opened it), so `hidden` clipped the
        // option list at the modal edge. The results list has its own scroll
        // container, so nothing here relies on the modal clipping.
        overflow="visible"
        // What the clip DID do was hide the square bottom corners of whichever
        // footer paints last against the 12px dialog radius. Round the last
        // one instead: mark every footer, then un-round any that another
        // footer follows, so this stays right however the two combine.
        sx={{
          // `inherit` reads the PARENT's computed radius, and cmdk's root div
          // sits between this dialog and the footers with a radius of 0 — so
          // relay it through, or every footer inherits square corners.
          //
          // The root is also the column that absorbs the theme's viewport cap
          // (components.modal.ts). This sheet keeps `overflow: visible`, so it
          // cannot fall back on the dialog scrolling: on a short window the
          // results list has to be the thing that gives, down to zero, while
          // the input row and the footers keep their height.
          '[cmdk-root]': {
            borderRadius: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minH: 0,
          },
          '[cmdk-list]': { minH: 0 },
          '[data-palette-footer]': { borderBottomRadius: 'inherit' },
          '[data-palette-footer]:has(~ [data-palette-footer])': { borderBottomRadius: 0 },
        }}
        p={0}
      >
        <Command
          label={isParentMode ? 'Select destination' : 'Global search'}
          // commandPalette.css draws the rule under the filter row only while
          // there is one; otherwise the top bar's border already separates.
          data-filter-row={showFilterRow ? '' : undefined}
          shouldFilter={false}
          loop
          {...(expanded
            ? {
                value: selectedValue,
                onValueChange: setSelectedValue,
                onKeyDown: handleCommandKeyDown,
                onPointerMove: handlePointerMove,
              }
            : {})}
        >
          {/* Top bar — ONE 32px row. Every control in it is 32px tall and the
              glyph sits in a 32px box, so the icon, the input's text box, the
              filter button and the `esc` cap share a baseline instead of each
              being centred on its own height. */}
          <HStack
            px={6}
            pt="18px"
            pb="14px"
            borderBottom="1px solid"
            borderColor="border.subtle"
            spacing={3}
          >
            {/* Asked for, not assumed: the variant comes from the shared
                glyph rule rather than a literal, so this magnifier and the
                rail's cannot be painted two different ways again. */}
            <Box
              boxSize="32px"
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <BowerIcon name="search" size="20px" gradient={gradientVariantForGlyph('search')} />
            </Box>
            <Command.Input
              ref={searchInputRef}
              value={query}
              onValueChange={setQuery}
              placeholder={placeholder}
              autoFocus
            />
            {showSpinner && <BowerSpinner size="xs" flexShrink={0} />}
            {canToggleFilterRow && (
              <IconButton
                aria-label={showFilterRow ? 'Hide filters' : 'Show filters'}
                aria-pressed={showFilterRow}
                // `color="inherit"` or the glyph never sees the button's
                // colour: MaterialIcon defaults to `text.secondary` and emits
                // its own `color` unless told to inherit, so the open and
                // closed states below rendered byte-identical.
                icon={<MaterialIcon name="tune" boxSize={4} color="inherit" />}
                onClick={handleFilterVisibilityToggle}
                // 32px VISUAL on a 44px TARGET. The design draws this button
                // at 32 to sit on the top bar's grid; WCAG 2.5.5 wants 44x44.
                // Both are satisfiable at once — the same trick `Toggle` in
                // ConnectorPickerButton uses for its 32x18 switch: keep the
                // painted box small and let padding carry the hit area past
                // it, then pull the extra back out of the layout with a
                // negative margin so the row's rhythm is unchanged.
                //
                // Safe HERE specifically because the button is isolated on
                // this row. The chat panel's header has three of these at a
                // 42px pitch, where the same expansion would overlap adjacent
                // hit areas and let them steal each other's clicks — worse for
                // real pointer accuracy than the 2px it would buy.
                boxSize="44px"
                minW="44px"
                m="-6px"
                sx={{
                  // The visible chip. `content-box` sizing on the background
                  // would be fragile across Chakra's button reset, so paint it
                  // with a pseudo-element and leave the button's own box bare.
                  background: 'transparent',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: '6px',
                    borderRadius: 'var(--chakra-radii-lg)',
                    background: 'var(--chakra-colors-brand-surface)',
                    transition: 'background 0.15s ease',
                  },
                  '& > *': { position: 'relative' },
                }}
                _hover={{
                  background: 'transparent',
                  '&::before': { background: 'var(--chakra-colors-bg-selection)' },
                }}
                variant="ghost"
                // The tint is always there, not just when the row is open —
                // the design gives this button a standing Twilight 50 fill so
                // it reads as the one accent in an otherwise neutral bar. Open
                // state is carried by `aria-pressed` and the darker glyph.
                color={showFilterRow ? 'brand.onSurface' : 'text.secondary'}
                flexShrink={0}
              />
            )}
            <Kbd size="row">esc</Kbd>
          </HStack>

          {showFilterRow && (
            <SearchScopeFilter
              hasCommands={expanded}
              scope={scope}
              onScopeChange={setScope}
              activeTypes={activeFilters}
              onTypesChange={setActiveFilters}
            />
          )}

          {/* Parent selection mode: flat list of destinations */}
          {isParentMode ? (
            <ParentModeList
              query={query}
              isFetching={isFetching}
              isShowingRecents={isShowingRecents}
              options={parentOptions}
              onSelect={handleParentSelect}
              failure={contentFailure}
              onRetry={handleRetryContent}
            />
          ) : (
            contentSection
          )}

          {/* Ask Bower pinned below the scrollable list so it's always reachable
              without scrolling past a long result set. Tab (handleCommandKeyDown)
              and this footer share `handleAskBird`. */}
          {expanded && <AskBirdFooter query={query} onActivate={handleAskBird} />}

          <SearchLiveRegion
            resultCount={resultCount}
            // No server total: it is a floor (a bounded candidate pool) that
            // grows as pages load, so "6 of 40" would read as exact and change.
            totalCount={undefined}
            debouncedQuery={debouncedQuery}
            failureMessage={announcedFailure && searchFailureMessage(announcedFailure)}
          />
        </Command>
      </ModalContent>
    </Modal>
  );
}
